import { useCallback, useContext, useState } from 'react';

import { useOnEnter, useOnEscape } from '@libs/hooks/useOnKeyDown';
import { ModalCard } from '@ui/components/ModalCard';
import { ThemeButton } from '@ui/components/ThemeButton';

import { RcfAttributes } from '@features/shared/api/typings';
import { RcfAttributesForm } from '@features/user/ui/RcfAttributesForm/RcfAttributesForm';
import { RcfUserSearch } from '@features/user/ui/RcfUserSearch/RcfUserSearch';
import { RcfUserSearchContext } from '@features/user/ui/RcfUserSearch/RcfUserSearchContext';

import styles from './RcfAttributesEditorModal.module.css';

type Props = {
  userName: string;
  value: RcfAttributes;
  onChange: (newValue: RcfAttributes) => void;
  onClose: () => void;
};
export function RcfAttributesEditorModal({ userName, value, onChange, onClose }: Props) {
  const { query, setQuery, rcfUsers, isLoading, hasNextPage, fetchNextPage } =
    useContext(RcfUserSearchContext);
  const [localValue, setLocalValue] = useState(value);

  const handleSetAttributesFromSearch = useCallback(
    (attributes: Partial<RcfAttributes>) => {
      const newValue = { ...localValue, ...attributes };
      setLocalValue(newValue);
    },
    [localValue, setLocalValue],
  );

  const handleClose = useCallback(() => {
    setQuery('');
    onClose();
  }, [setQuery, onClose]);

  const handleSave = useCallback(() => {
    onChange({
      ...localValue,
    });
    handleClose();
  }, [localValue, onChange, handleClose]);

  const handleSetNameAsQuery = useCallback(() => {
    setQuery(userName);
  }, [setQuery, userName]);

  useOnEscape(handleClose);

  useOnEnter(handleSave);

  return (
    <ModalCard
      className={styles.modal}
      contentClassName={styles.content}
      title="Редактирование ФШР ID"
      onClose={handleClose}
    >
      <div className={styles.title}>
        <span>
          Участник: <span className={styles.userName}>{userName}</span>
        </span>
        <ThemeButton compact onClick={handleSetNameAsQuery}>
          Подставить имя в поиск
        </ThemeButton>
      </div>

      <RcfAttributesForm value={localValue} onChange={setLocalValue} />

      <div className={styles.buttons}>
        <ThemeButton inverted onClick={handleClose}>
          Отменить
        </ThemeButton>
        <ThemeButton onClick={handleSave}>Сохранить</ThemeButton>
      </div>

      <RcfUserSearch
        query={query}
        onQueryChange={setQuery}
        rcfUsers={rcfUsers}
        isLoading={isLoading}
        onSetRcfAttributes={handleSetAttributesFromSearch}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
      />
    </ModalCard>
  );
}
